import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SiteLayout, PageHero, SectionTag, SERVICES, SERVICE_PAGES, useSEO } from "./shared";
import { SERVICE_CONTENT } from "./serviceContent";

const SERVICE_DETAILS = {
  "fire-alarm-systems": ["Conventional & addressable panels", "Smoke, heat & manual call point devices", "Testing, programming and commissioning"],
  "fire-hydrant-systems": ["Hydrant piping & landing valves", "Hose reels & hose cabinets", "Pressure testing and commissioning"],
  "sprinkler-systems": ["Sprinkler piping & sprinkler heads", "Valves, supports & alarm devices", "Pressure testing before handover"],
  "fire-pump-systems": ["Main electric & diesel engine pumps", "Jockey pumps & pump controllers", "Pump-room piping and installation"],
  "fire-extinguishers": ["Multiple extinguisher types (ABC, CO2, etc.)", "Site-wise placement planning", "Inspection, servicing & refilling support"],
  "emergency-lighting-exit-signage": ["Non-maintained & maintained emergency lights", "Illuminated exit signage", "Evacuation route coverage"],
  "cctv-systems": ["Camera design & structured cabling", "Installation and monitoring setup", "Coverage planning for premises security"],
  "access-control-systems": ["Card, biometric and keypad access", "Restricted and monitored entry points", "Integration with premises security"],
  "public-address-systems": ["Voice evacuation integration", "Zone-wise public announcements", "Signalling integration with fire alarm"],
  "amc": ["Scheduled preventive maintenance", "Testing & compliance support", "Coverage across all installed systems"],
};

export default function Services() {
  useSEO(
    "Fire Protection Services in Tripura | SecureFlame Fire Systems",
    "Fire alarm, hydrant, sprinkler, fire pump, extinguisher, emergency lighting, CCTV, access control, PA systems and AMC — complete fire protection engineering services from SecureFlame across Tripura."
  );
  const [active, setActive] = useState(null);

  return (
    <SiteLayout>
      <PageHero eyebrow="Our Services" title="Engineering Every Layer of Fire Protection" subtitle="From detection to suppression, SecureFlame designs, supplies, installs, tests, commissions and maintains fire protection systems for every building type." />

      <section className="max-w-7xl mx-auto px-6 py-16 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s) => (
            <div key={s.slug} className={`group p-7 rounded-sm border bg-card hover:shadow-lg transition-all duration-200 flex flex-col ${s.slug === "amc" ? "border-gold/50 bg-gold/5" : "border-border hover:border-primary/40"}`} data-testid={`services-card-${s.slug}`}>
              <s.icon className="text-primary mb-4" size={30} />
              <h3 className="font-heading font-bold text-base text-secondary">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed flex-1">{s.desc}</p>
              <button onClick={() => setActive(s)} className="mt-4 text-sm font-semibold text-primary hover:underline flex items-center gap-1.5 self-start" data-testid={`services-learnmore-${s.slug}`}>
                Learn More <ArrowRight size={14} />
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-16 md:pb-20">
        <SectionTag>Quick Comparison</SectionTag>
        <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-secondary mb-6">Compare Our Services at a Glance</h2>
        <div className="bg-card border border-border rounded-sm overflow-hidden overflow-x-auto" data-testid="services-comparison-table">
          <Table className="min-w-[840px]">
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Service / System</TableHead>
                <TableHead>Purpose</TableHead>
                <TableHead>Typical Application</TableHead>
                <TableHead className="text-right">Learn More</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {SERVICE_PAGES.map((s) => {
                const content = SERVICE_CONTENT[s.slug];
                const apps = (content?.suitableFor || []).slice(0, 3).join(", ");
                const hasMore = (content?.suitableFor || []).length > 3;
                return (
                  <TableRow key={s.slug} data-testid={`comparison-row-${s.slug}`}>
                    <TableCell className="font-medium text-secondary whitespace-nowrap">
                      <span className="flex items-center gap-2"><s.icon size={16} className="text-primary shrink-0" /> {s.title}</span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-xs">{s.desc}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{apps}{hasMore ? " + more" : ""}</TableCell>
                    <TableCell className="text-right">
                      <Link to={`/services/${s.slug}`} className="text-sm font-semibold text-primary hover:underline whitespace-nowrap" data-testid={`comparison-learnmore-${s.slug}`}>Learn More</Link>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </section>

      <section className="bg-secondary text-white">
        <div className="max-w-4xl mx-auto px-6 py-14 text-center">
          <CalendarCheck className="text-gold mx-auto mb-4" size={32} />
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Also Featured: Annual Maintenance Contracts</h2>
          <p className="mt-3 text-slate-300 max-w-xl mx-auto">Keep every installed system tested, compliant and ready year-round with a scheduled AMC.</p>
          <Link to="/amc"><Button size="lg" className="mt-6 rounded-sm bg-primary hover:bg-primary/90 font-semibold gap-2" data-testid="services-amc-btn">EXPLORE AMC <ArrowRight size={16} /></Button></Link>
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="service-detail-modal">
          {active && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2.5 text-primary mb-1"><active.icon size={24} /></div>
                <DialogTitle className="font-heading text-secondary">{active.title}</DialogTitle>
                <DialogDescription className="text-muted-foreground">{active.desc}</DialogDescription>
              </DialogHeader>
              <ul className="space-y-2 text-sm text-secondary">
                {(SERVICE_DETAILS[active.slug] || []).map((f) => (
                  <li key={f} className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span> {f}</li>
                ))}
              </ul>
              <DialogFooter>
                <Link to={`/contact?type=quote&service=${active.slug}`} className="w-full">
                  <Button className="w-full rounded-sm bg-primary hover:bg-primary/90 font-semibold" data-testid="service-detail-request-btn">REQUEST THIS SERVICE</Button>
                </Link>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </SiteLayout>
  );
}
